import React from "react";

export type UiTestScenario = {
  name: string;
  feature: string;
  why: string;
  origin: string;
  outcome: string;
  detail?: string;
  gif_url?: string;
  mp4_url?: string;
};

export type UiTestRun = {
  job_id: string;
  status: string;
  head_sha: string;
  preview_url?: string;
  started_at: string;
  updated_at: string;
  error?: string;
  scenarios: UiTestScenario[];
};

export function httpUrl(value?: string) {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    if (url.protocol === "http:" || url.protocol === "https:") return value;
  } catch {
    return undefined;
  }
  return undefined;
}

/// Map a UI-test status onto the shared `.state` pill tones.
export function uiTestStatusClass(status: string) {
  if (status === "passed" || status === "not_applicable") return "active";
  if (status === "failed" || status === "error" || status === "blocked") return "failed";
  if (status === "partial") return "stale";
  return "";
}

export function uiTestOutcomeCounts(runs: UiTestRun[]) {
  let passed = 0;
  let failed = 0;
  for (const run of runs) {
    for (const scenario of run.scenarios) {
      if (scenario.outcome === "passed") passed += 1;
      else if (scenario.outcome === "failed" || scenario.outcome === "error") failed += 1;
    }
  }
  return { passed, failed };
}

function shortHead(sha?: string) {
  return sha ? sha.slice(0, 8) : "—";
}

function relativeTime(value?: string) {
  if (!value) return "—";
  const seconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (Math.abs(seconds) < 60) return formatter.format(seconds, "second");
  const minutes = Math.round(seconds / 60);
  if (Math.abs(minutes) < 60) return formatter.format(minutes, "minute");
  return formatter.format(Math.round(minutes / 60), "hour");
}

function counted(value: number, singular: string, plural = `${singular}s`) {
  return `${value} ${value === 1 ? singular : plural}`;
}

export function PullRequestTests({ runs, headSha }: { runs: UiTestRun[]; headSha?: string }) {
  const { passed, failed } = uiTestOutcomeCounts(runs);
  const scenarios = runs.reduce((count, run) => count + run.scenarios.length, 0);
  return <section className="activity-card detail-section pr-ui-tests">
    <p className="eyebrow">UI TESTS</p>
    <h2>{counted(runs.length, "UI test run")}</h2>
    <p className="section-description">
      {scenarios === 0
        ? <>Browser scenarios recorded from <code>netra: test</code>.</>
        : <>{passed} passed · {failed} failed or errored · {counted(scenarios, "scenario")}</>}
    </p>
    <div className="run-cards">
      {runs.length === 0 ? <p>No UI tests recorded for this pull request. Comment <code>netra: test</code> on the pull request to run them.</p> : runs.map((run) => {
        const preview = httpUrl(run.preview_url);
        const current = Boolean(headSha) && run.head_sha === headSha;
        return <article key={run.job_id}>
          <div className="run-card-head">
            <div>
              <span className={`state ${uiTestStatusClass(run.status)}`}>{run.status}</span>
              <strong>{shortHead(run.head_sha)}{current ? " · current head" : ""}</strong>
            </div>
            <span>{relativeTime(run.started_at)}</span>
          </div>
          {preview && <p><a href={preview} target="_blank">Open preview ↗</a></p>}
          {run.error && <pre className="failure">{run.error}</pre>}
          {run.scenarios.length === 0 ? <p>No scenarios ran.</p> : <div className="ui-test-scenarios">
            {run.scenarios.map((scenario) => {
              const gif = httpUrl(scenario.gif_url);
              const mp4 = httpUrl(scenario.mp4_url);
              const meta = [scenario.feature, scenario.origin].filter(Boolean).join(" · ");
              return <div className="ui-test-scenario" key={scenario.name}>
                <div className="ui-test-scenario-head">
                  <span className={`state ${uiTestStatusClass(scenario.outcome)}`}>{scenario.outcome}</span>
                  <strong>{scenario.name}</strong>
                </div>
                {meta && <p>{meta}</p>}
                {scenario.why && <p>{scenario.why}</p>}
                {scenario.detail && <p>{scenario.detail}</p>}
                {(gif || mp4) && <p className="ui-test-links">
                  {gif && <a href={gif} target="_blank">GIF ↗</a>}
                  {mp4 && <a href={mp4} target="_blank">MP4 ↗</a>}
                </p>}
              </div>;
            })}
          </div>}
        </article>;
      })}
    </div>
  </section>;
}
